import { useCallback, useEffect, useMemo } from "react";
import { NativeScrollEvent, NativeSyntheticEvent } from "react-native";
import { SharedValue } from "react-native-reanimated";
import {
	GatheringHeaderInfoFragmentFragment,
	StickerPackFragmentFragment,
} from "src/api/graphql/__generated__/graphql";
import useFooterGradientHeight from "src/components/FooterGradient/hooks/useFooterGradientHeight";
import ListFooterComponent from "src/components/ListFooterComponent";
import { useStickerRowItemAllocation } from "src/components/StickerRow";
import parseStickerTemplateUrl from "src/helpers/parseV0TemplateUrl/parseStickerTemplateUrl";
import useDim from "src/hooks/useDim";
import { List, useSafeArea } from "src/swsh-native";
import { VirtualList } from "src/swsh-native/List";
import useGatheringPublicFullContext from "../../hooks/useGatheringPublicFullContext";
import { useGatheringPublicFullStore } from "../../hooks/useGatheringPublicFullStore";
import gatheringPublicFullBodyGetItemType from "./helpers/gatheringPublicFullBodyGetItemType";
import gatheringPublicFullBodyKeyExtractor from "./helpers/gatheringPublicFullBodyKeyExtractor";
import useGatheringFullPublicPhotos from "./hooks/useGatheringFullPublicPhotos";
import useGatheringFullPublicTopPhotos from "./hooks/useGatheringFullPublicTopPhotos";
import useGatheringPublicFullBodyRenderItem from "./hooks/useGatheringPublicFullBodyRenderItem";
import useGatheringPublicFullBodySpacing from "./hooks/useGatheringPublicFullBodySpacing";
import { GatheringPublicFullBodyItem, GatheringPublicFullBodyRow } from "./types";

const GatheringPublicFullBody = ({
	scrollOffset,
	stickerPack,
	thumbnail,
}: {
	scrollOffset: SharedValue<number>;
	stickerPack: StickerPackFragmentFragment | null | undefined;
	thumbnail: GatheringHeaderInfoFragmentFragment["thumbnail"];
}) => {
	const gatheringId = useGatheringPublicFullStore((state) => state.gatheringId);
	const {
		photos,
		fetchMore: photosFetchMore,
		loading: photosLoading,
		refreshing: photosRefreshing,
		refresh: photosRefresh,
	} = useGatheringFullPublicPhotos(gatheringId);
	const {
		topPhotos,
		refreshing: topPhotosRefreshing,
		refresh: topPhotosRefresh,
	} = useGatheringFullPublicTopPhotos({ gatheringId });
	const { store } = useGatheringPublicFullContext();
	useEffect(() => {
		store.setState({
			headerPhotos: photos.slice(0, 14),
		});
	}, [store, photos]);
	useEffect(() => {
		store.setState({
			hasTopPhotos: topPhotos.length > 0,
		});
	}, [store, topPhotos]);

	const handleScroll = useCallback(
		(event: NativeSyntheticEvent<NativeScrollEvent>) => {
			scrollOffset.value = event.nativeEvent.contentOffset.y;
		},
		[scrollOffset],
	);
	const photosMode = useGatheringPublicFullStore((state) => state.photosMode);
	const dim = useDim();
	const numColumns = useMemo(() => {
		return Math.min(Math.max(Math.floor(dim.width / 250), 2), 8);
	}, [dim.width]);
	const { spacing } = useGatheringPublicFullBodySpacing();
	const { getParsedSticker } = useStickerRowItemAllocation({
		numColumns,
		padding: spacing,
		stickers: (stickerPack?.stickers ?? []).map((sticker) => ({
			url: parseStickerTemplateUrl({ readUrl: sticker.readUrl }),
		})),
	});
	const data = useMemo<GatheringPublicFullBodyItem[]>(() => {
		const curPhotos = photosMode === "Top" ? topPhotos : photos;
		const data: GatheringPublicFullBodyItem[] = [];

		data.push({
			variant: "Header",
			itemKey: "Header",
		});

		const rows: GatheringPublicFullBodyRow[] = [];
		for (let i = 0; i < curPhotos.length; i += numColumns) {
			const data: GatheringPublicFullBodyRow["data"] = curPhotos
				.slice(i, i + numColumns)
				.map((photo, j) => ({
					itemKey: photo.photoId,
					variant: "Photo",
					photo,
					sticker: getParsedSticker({
						rowIndex: rows.length,
						columnIndex: j,
					}),
				}));
			for (let j = data.length; j < numColumns; j++) {
				data.push({
					itemKey: j.toString(),
					variant: "Empty",
				});
			}
			rows.push({
				itemKey: data.map((item) => item.itemKey).join(","),
				variant: "Row",
				data,
			});
		}
		data.push(...rows);
		return data;
	}, [photosMode, topPhotos, photos, numColumns, getParsedSticker]);
	const safeArea = useSafeArea();
	const { gatheringPublicFullBodyRenderItem } = useGatheringPublicFullBodyRenderItem({
		numColumns,
		thumbnail,
	});
	const footerHeight = useFooterGradientHeight();

	return (
		<List
			onScroll={handleScroll}
			WebListComponent={VirtualList}
			onEndReachedThreshold={2}
			refreshing={photosRefreshing || topPhotosRefreshing}
			onRefresh={async () => {
				await Promise.all([photosRefresh(), topPhotosRefresh()]);
			}}
			data={data}
			keyExtractor={gatheringPublicFullBodyKeyExtractor}
			getItemType={gatheringPublicFullBodyGetItemType}
			renderItem={gatheringPublicFullBodyRenderItem}
			estimatedItemSize={400}
			onEndReached={photosFetchMore}
			ListFooterComponent={<ListFooterComponent loading={photosLoading} />}
			contentContainerStyle={{
				paddingBottom: safeArea.bottom + (photos.length > 0 ? footerHeight : 0),
			}}
		/>
	);
};
export default GatheringPublicFullBody;
